import { Component } from 'preact'
import Phrase, { fetchPhrase } from '../../libs/translationsUtils/phrase'
import GlobalClick from '../interactive/documentClick'

import ButtonControl from '../../preact-components/button-control'
import Button from '../../control-components/ui-components/Button'
import AttachmentList from '@insided/shared/uikit/Editor/Attachments/AttachmentList'
import { fromEditorFormat, toEditorFormat } from '@insided/shared/uikit/Editor/contentConverter'
import AttachmentUploader, { FileSizeError, TotalFileSizeError, FileTypeError } from '@insided/shared/uikit/Editor/Attachments/AttachmentUploader'
import ErrorList from '@insided/shared/uikit/Editor/ErrorList'
import Poll from '@insided/shared/uikit/Editor/Poll'
import classNames from 'classnames'
import AddTopicCardModal from '@insided/shared/uikit/Editor/AddTopicCardModal'
import { prepareAttachments, prepareAttachmentsForState } from '@insided/shared/uikit/Editor/Attachments/utils'
import { compareHtml, requiredPhrases } from '@insided/shared/uikit/Editor/utils'
import { observeMentions } from '@insided/shared/uikit/Editor/observeMentions'

let EmojiSelector
let CKEditor

/**
 * @deprecated
 * ```
 * import Editor from '@insided/shared/uikit/Editor'
 * ```
 */
class Editor extends Component {
  constructor (props) {
    super(props)
    require('../../utils/replaceWithPolyfill')
    require('../../utils/removePolyfill')
    require('../../utils/childNodeBeforePolyfill')
    require('../../utils/parentNodeAppendPolyfill')
    if (props.CKEditor) CKEditor = props.CKEditor
    this.state = {
      attachments: props.attachments && props.attachments.length ? prepareAttachmentsForState(props.attachments) : [],
      attachmentErrors: [],
      editorErrors: [],
      attachmentToProcess: 0,
      imageToProcess: 0,
      editor: null,
      enablePoll: props.poll != null,
      poll: props.poll != null
        ? props.poll
        : {
            question: '',
            options: []
          },
      showTopicCardModal: false
    }

    this.initialData = props.data
    this.editorContent = props.data
    this.initialEditorContent = toEditorFormat(props.data)
    this.editorContent = this.initialEditorContent
    this.submitContent = this.submitContent.bind(this)
    this.setNewEditorInstance = this.setNewEditorInstance.bind(this)
    this.togglePoll = this.togglePoll.bind(this)
    this.editPoll = this.editPoll.bind(this)
    this.getInitialEditorContent = this.getInitialEditorContent.bind(this)
    this.showNotification = this.showNotification.bind(this)
    this.onEmojiSelect = this.onEmojiSelect.bind(this)
    this.onTopicCardClicked = this.onTopicCardClicked.bind(this)
    this.onAdd = this.onAdd.bind(this)
    this.onClose = this.onClose.bind(this)
    this.onDismissError = this.onDismissError.bind(this)
    this.getCancelButton = this.getCancelButton.bind(this)
    this.getSubmitButton = this.getSubmitButton.bind(this)

    this.attachmentUploader = new AttachmentUploader()
  }

  getEditorContent () {
    if (this.editorContent === undefined) {
      return this.initialData
    }
    return this.editorContent
  }

  componentWillReceiveProps ({ attachments }) {
    attachments = attachments || []
    if (this.state.attachments.length) return
    this.setState({
      attachments: prepareAttachmentsForState(attachments)
    })
  }

  preloadPhrases (cb) {
    const noop = () => {}
    requiredPhrases.forEach(([m, p], idx) => {
      if (idx === requiredPhrases.length - 1) return fetchPhrase(m, p, noop, cb)
      fetchPhrase(m, p, noop)
    })
  }

  componentDidMount () {
    this.preloadPhrases(() => {
      if (CKEditor) return this.setState({ loaded: true })

      require.ensure(['../../libs/ckeditor/ckeditor.js', '../../libs/emoji/selector'], (require) => {
        CKEditor = require('../../libs/ckeditor/ckeditor.js').default
        EmojiSelector = require('../../libs/emoji/selector').default
        this.setState({ loaded: true })
      }, 'ckeditor')
    })
  }

  attachmentError (filename, errorPhrase) {
    this.setState(({ attachmentToProcess, attachmentErrors }) => ({
      attachmentToProcess: attachmentToProcess - 1,
      attachmentErrors: attachmentErrors.concat({ filename, errorPhrase })
    }))
  }

  async uploadFile (file) {
    let uploadData

    const attachmentId = this.attachmentUploader.getUniqueAttachmentId()

    // Add empty attachment to show the loader
    this.updateAttachments(
      this.state.attachments.concat(
        {
          attachmentId,
          source: '',
          source_thumb: '',
          type: this.attachmentUploader.getType(file),
          file_original_name: file.name,
          file_size: file.size,
          file_uuid: '',
          url: '',
          filename: file.name
        }
      )
    )

    try {
      uploadData = await this.attachmentUploader.uploadFile(attachmentId, file)
    } catch (err) {
      if (err instanceof FileSizeError) {
        this.attachmentError(file.name, 'js.uploader.error_file_size')
      } else if (err instanceof TotalFileSizeError) {
        this.attachmentError(file.name, 'js.attachments.error_total_file_size_per_post')
      } else if (err instanceof FileTypeError) {
        this.attachmentError(file.name, 'js.uploader.error_file_type')
      } else {
        this.attachmentError(file.name, 'js.uploader.error_on_upload')
      }
      this.updateAttachments(
        this.state.attachments.filter(
          ({ attachmentId: id }) => id !== attachmentId)
      )
      return
    }

    this.setState({ attachmentToProcess: this.state.attachmentToProcess - 1 }, () => {
      this.updateAttachments(
        this.state.attachments.map(a => {
          if (a.attachmentId !== attachmentId) return a
          return Object.assign(
            a,
            {
              url: uploadData.source || uploadData.download_url,
              source: uploadData.source || uploadData.download_url,
              source_thumb: uploadData.source_thumb || uploadData.download_url,
              file_uuid: uploadData.uuid
            }
          )
        })
      )
    })
  }

  async uploadAttachments (files) {
    this.setState({ attachmentToProcess: files.length })

    for (let i = 0; i < files.length; i++) {
      await this.uploadFile(files[i])
    }
  }

  updateAttachments (attachments) {
    const { imageToProcess, attachmentToProcess } = this.state

    this.setState({ attachments })

    if (!this.props.onChange) return
    this.props.onChange(this.getEditorContent(), prepareAttachments(attachments), (!!imageToProcess || !!attachmentToProcess))
  }

  deleteAttachment (index) {
    this.updateAttachments(
      this.state.attachments.filter((_, idx) => idx !== index)
    )
  }

  submitContent (content, attachments, onSubmit) {
    if (this.props.isGuest) {
      const event = new window.CustomEvent('GuestUserAction')
      return window.dispatchEvent(event)
    }

    onSubmit(content, prepareAttachments(attachments), (!!this.state.imageToProcess || !!this.state.attachmentToProcess))
  }

  setNewEditorInstance (instance) {
    this.setState({ editor: instance }, () => {
      this.state.editor.on('filesToProcessCountChange', (evt) => {
        this.setState({ imageToProcess: evt.data.count })
      })

      this.state.editor.on('pollButtonClicked', () => {
        this.togglePoll()
      })

      if (!this.props.disableMentions) {
        observeMentions()
      }
    })
  }

  togglePoll () {
    const { isControl, onPollUpdate } = this.props
    this.setState(({ enablePoll }) => ({
      enablePoll: !enablePoll,
      poll: { question: '', options: [] }
    }))
    if (isControl) onPollUpdate(null)
  }

  editPoll (newValue) {
    const newPoll = { ...this.state.poll, ...newValue }
    this.setState({ poll: newPoll }, this.props.onPollUpdate(newPoll))
  }

  getInitialEditorContent () {
    const { errors } = this.props
    const hasErrors = Array.isArray(errors) && errors.length > 0
    return hasErrors ? null : this.initialEditorContent
  }

  showNotification (category, phrase) {
    this.setState(({ editorErrors }) => ({
      editorErrors: editorErrors.concat({ category, phrase })
    }))
  }

  onEmojiSelect (evt) {
    const { emojiCallback } = this.state

    if (emojiCallback) {
      return this.setState({
        emojiCallback: null
      })
    }
    this.setState({
      emojiCallback: evt.data.onSelect
    })
  }

  onTopicCardClicked (evt) {
    const { data } = evt
    this.setState({ showTopicCardModal: true, onAddTopic: data.onAdd })
  }

  onAdd(topic) {
    this.setState({ showTopicCardModal: false }, () => this.state.onAddTopic(topic))
  }

  onClose() {
    this.setState({ showTopicCardModal: false })
  }

  onDismissError() {
    this.setState({ attachmentErrors: [] })
  }

  getCancelButton() {
    const { isControl, onCancel, cancelPhrase } = this.props
    return isControl
      ? <ButtonControl
          variant='secondary'
          onClick={() => onCancel()}
          title={<Phrase p={cancelPhrase || 'Cancel'} />}
        />
      : <Button
          secondary
          link
          onClick={() => onCancel()}
        >
        <Phrase p={cancelPhrase || 'Cancel'} />
      </Button>
  }

  getSubmitButton() {
    const { isControl, btnText, btnPhrase, isLoading, onCancel, onSubmit } = this.props
    const submitButtonText = btnText || <Phrase p={btnPhrase || 'Send'} />

    return (isControl
      ? <ButtonControl
          disabled={isLoading}
          variant={onCancel ? 'success' : 'primary'}
          onClick={() => this.submitContent(this.getEditorContent(), this.state.attachments, onSubmit)}
          isLoading={isLoading}
          title={onCancel ? 'Save changes' : submitButtonText}
        />
      : <Button
          primary
          loading={isLoading}
          disabled={isLoading}
          onClick={() => this.submitContent(this.getEditorContent(), this.state.attachments, onSubmit)}
        >
        {submitButtonText}
      </Button>)
  }

  render() {
    const {
      onChange = () => {}, onCancel, onSubmit, input, isControl, autoFocus, errors, cssLink, disableFileAttachments,
      disableMentions, placeholder, canHavePoll, classes, error, profile
    } = this.props
    const { editorErrors } = this.state
    const additionalClasses = classes || []

    return (
      <div className='html-editor__wrapper'>
        <ErrorList errors={errors} />
        <div className={classNames({ 'validation-error': error }, 'html-editor', ...additionalClasses, isControl && 'html-editor--control')}>
          <ErrorList errors={
            editorErrors.map(({ category, phrase }) => ({
              errorMessage: <Phrase
                m={category} p={phrase} count={{
                  allowed_files_size: '5MB'
                }}
                            />
            }))
          }
          />
          {CKEditor &&
            <CKEditor
              disableFileAttachments={disableFileAttachments}
              disableMentions={disableMentions}
              canHavePoll={canHavePoll}
              isControl={isControl}
              autoFocus={autoFocus}
              onInstanceReady={instance => this.setNewEditorInstance(instance)}
              data={this.getInitialEditorContent()}
              onFileAttachment={evt => this.uploadAttachments(evt.data.files)}
              isGuest={this.props.isGuest}
              placeholder={placeholder}
              cssLink={cssLink}
              profile={profile}
              showNotification={this.showNotification}
              onEmojiSelect={this.onEmojiSelect}
              onTopicCardClicked={this.onTopicCardClicked}
              onFocus={() => {}}
              onChange={content => {
                content = content.replace(/<oembed(.*)\/>/g, '<oembed$1></oembed>')
                if (this.editorContent && compareHtml(content, this.editorContent)) return

                content = fromEditorFormat(content)
                content = content.replace(/\n/g, '')
                if (this.editorContent !== undefined) {
                  onChange(content, prepareAttachments(this.state.attachments), (!!this.state.imageToProcess || !!this.state.attachmentToProcess))
                }
                this.editorContent = content
                if (!input) return
                document.querySelector(input).value = content
              }}
            />}

          {/* This empty div is needed to capture a click outside of emoji box (on editable area) and close it */}
          {this.state.emojiCallback && EmojiSelector &&
            <div className='emoji-selector-positioner-background' />}

          {
            this.state.emojiCallback && EmojiSelector &&
              <GlobalClick
                onGlobalClick={() => {
                  this.setState({ emojiCallback: null })
                }} className='emoji-selector-positioner'
              >
                <EmojiSelector onSelect={(unicode, shortname) => {
                  const emojiCallback = this.state.emojiCallback
                  this.setState({ emojiCallback: null })
                  emojiCallback(unicode, shortname)
                }}
                />
              </GlobalClick>
          }

          {
            this.state.showTopicCardModal && <AddTopicCardModal
              isOpen={this.state.showTopicCardModal}
              onAdd={this.onAdd}
              onClose={this.onClose}
                                             />
          }
        </div>

        {
          this.state.enablePoll
            ? (
              <Poll
                {...this.state.poll}
                onDelete={this.togglePoll}
                onChange={(newValue) => this.editPoll(newValue)}
              />
              )
            : null
        }

        <AttachmentList
          attachments={this.state.attachments}
          errors={this.state.attachmentErrors}
          onDelete={idx => this.deleteAttachment(idx)}
          onDismissError={this.onDismissError}
        />

        <div className='html-editor__buttons'>
          {onSubmit && this.getSubmitButton()}

          {onCancel && this.getCancelButton()}
        </div>
      </div>
    )
  }
}

export default Editor
